import React from 'react';

type ScrollSource = 'header' | 'body' | 'bottom';

export function useBottomScrollbar(itemCount: number, contentWidthKey: number) {
    const headerScrollRef = React.useRef<HTMLDivElement | null>(null);
    const bodyScrollRef = React.useRef<HTMLDivElement | null>(null);
    const bottomScrollRef = React.useRef<HTMLDivElement | null>(null);
    const [bottomScrollbar, setBottomScrollbar] = React.useState({
        contentWidth: 0,
        fixedLeft: 0,
        fixedWidth: 0,
        show: false,
    });

    const syncScrollLeft = React.useCallback((scrollLeft: number, source: ScrollSource) => {
        const targets = [
            { key: 'header', element: headerScrollRef.current },
            { key: 'body', element: bodyScrollRef.current },
            { key: 'bottom', element: bottomScrollRef.current },
        ];

        targets.forEach(({ key, element }) => {
            if (key === source || !element) {
                return;
            }

            if (Math.abs(element.scrollLeft - scrollLeft) > 1) {
                element.scrollLeft = scrollLeft;
            }
        });
    }, []);

    const handleScrollSync = React.useCallback((source: ScrollSource) => (event: React.UIEvent<HTMLDivElement>) => {
        syncScrollLeft(event.currentTarget.scrollLeft, source);
    }, [syncScrollLeft]);

    React.useEffect(() => {
        const header = headerScrollRef.current;
        const body = bodyScrollRef.current;
        if (!header || !body) return;

        header.scrollLeft = 0;
        body.scrollLeft = 0;
        if (bottomScrollRef.current) {
            bottomScrollRef.current.scrollLeft = 0;
        }
    }, [contentWidthKey]);

    const measureBottomScrollbar = React.useCallback(() => {
        const body = bodyScrollRef.current;

        if (!body) {
            return;
        }

        const contentWidth = Math.ceil(body.scrollWidth);
        const viewportWidth = Math.ceil(body.clientWidth);
        const bodyRect = body.getBoundingClientRect();
        const fixedLeft = Math.max(0, Math.floor(bodyRect.left));
        const fixedWidth = Math.max(0, Math.min(viewportWidth, Math.ceil(window.innerWidth - fixedLeft)));

        setBottomScrollbar(prev => {
            const show = contentWidth - viewportWidth > 1 && fixedWidth > 0;

            if (
                prev.contentWidth === contentWidth &&
                prev.fixedLeft === fixedLeft &&
                prev.fixedWidth === fixedWidth &&
                prev.show === show
            ) {
                return prev;
            }

            return {
                contentWidth,
                fixedLeft,
                fixedWidth,
                show,
            };
        });
    }, []);

    React.useEffect(() => {
        measureBottomScrollbar();

        const body = bodyScrollRef.current;

        if (!body || typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measureBottomScrollbar);
            window.addEventListener('scroll', measureBottomScrollbar, true);

            return () => {
                window.removeEventListener('resize', measureBottomScrollbar);
                window.removeEventListener('scroll', measureBottomScrollbar, true);
            };
        }

        const observer = new ResizeObserver(measureBottomScrollbar);
        observer.observe(body);
        window.addEventListener('resize', measureBottomScrollbar);
        window.addEventListener('scroll', measureBottomScrollbar, true);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measureBottomScrollbar);
            window.removeEventListener('scroll', measureBottomScrollbar, true);
        };
    }, [contentWidthKey, itemCount, measureBottomScrollbar]);

    return {
        headerScrollRef,
        bodyScrollRef,
        bottomScrollRef,
        bottomScrollbar,
        handleScrollSync,
    };
}
